import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, SparkIcon, ProgressCircle, Spinner as SparkSpinner, sparkToast } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { useDatasetExport } from '@/features/datasets/hooks/useDatasetExport';
import '@/features/datasets/components/DatasetExportControl.css';

const EXPORT_ICON_PROGRESS_SIZE = 22;

/**
 * Export dropdown (JSON/CSV for classification) or single JSON export button.
 * Pair with {@link useDatasetExport} for shared download behavior.
 */
export function DatasetExportControl({
    onExport,
    isClassification = false,
    disabled = false,
    exporting = false,
    exportPercent = null,
    dropdownOpen = false,
    onDropdownOpenChange,
    buttonClassName = 'fs-unmask edit-columns-button',
    menuPlacement = 'dropdown',
}) {
    const exportIcon = exporting ? (
        typeof exportPercent === 'number'
            ? <ProgressCircle status="in-progress" value={Math.min(100, Math.round(exportPercent))} />
            : <SparkSpinner size="small" />
    ) : (
        <SparkIcon slug="download" size="small" />
    );

    const menuItems = useMemo(
        () => [
            {
                key: 'json',
                label: 'Export as JSON',
                disabled: exporting,
                onClick: () => onExport('json'),
            },
            {
                key: 'csv',
                label: 'Export as CSV',
                disabled: exporting,
                onClick: () => onExport('csv'),
            },
        ],
        [exporting, onExport]
    );

    if (isClassification && menuPlacement === 'inline') {
        return (
            <SparkExportMenu
                menuItems={menuItems}
                exportIcon={exportIcon}
                disabled={disabled}
                exporting={exporting}
                buttonClassName={buttonClassName}
                panelPlacement="above"
            />
        );
    }

    if (isClassification) {
        return (
            <SparkExportMenu
                menuItems={menuItems}
                exportIcon={exportIcon}
                disabled={disabled}
                exporting={exporting}
                dropdownOpen={dropdownOpen}
                onDropdownOpenChange={onDropdownOpenChange}
                buttonClassName={buttonClassName}
                panelPlacement="below"
            />
        );
    }

    return (
        <Button
            variant="tertiary"
            size="small"
            type="button"
            className={buttonClassName}
            disabled={disabled || exporting}
            startIcon={exportIcon}
            onClick={() => onExport('json')}
        >
            Export
        </Button>
    );
}

function mapMenuItemsToDropdownItems(menuItems, onClose) {
    return menuItems.map((item) => ({
        label: item.label,
        state: item.disabled ? 'disabled' : 'default',
        onClick: () => {
            item.onClick?.();
            onClose?.();
        },
    }));
}

function SparkExportMenu({
    menuItems,
    exportIcon,
    disabled,
    exporting,
    dropdownOpen,
    onDropdownOpenChange,
    buttonClassName,
    panelPlacement,
}) {
    const containerRef = useRef(null);
    const isControlled = dropdownOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = (isControlled ? dropdownOpen : internalOpen) && !exporting;

    const setOpen = (next) => {
        if (!isControlled) {
            setInternalOpen(next);
        }
        onDropdownOpenChange?.(next);
    };

    useEffect(() => {
        if (!exporting) return;
        if (isControlled) {
            onDropdownOpenChange?.(false);
        } else {
            setInternalOpen(false);
        }
    }, [exporting, isControlled, onDropdownOpenChange]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (containerRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const closeMenu = () => setOpen(false);
    const dropdownItems = mapMenuItemsToDropdownItems(menuItems, closeMenu);

    const rootClass = [
        'dataset-export-spark-menu',
        panelPlacement === 'above'
            ? 'dataset-export-spark-menu--above'
            : 'dataset-export-spark-menu--below',
    ].join(' ');

    return (
        <div ref={containerRef} className={rootClass}>
            <Button
                variant="tertiary"
                size="small"
                type="button"
                className={buttonClassName}
                disabled={disabled || exporting}
                startIcon={exportIcon}
                aria-expanded={isOpen}
                onClick={() => {
                    if (!exporting) setOpen(!isOpen);
                }}
            >
                Export
            </Button>
            {isOpen ? (
                <div className="dataset-export-spark-menu__panel">
                    <Dropdown
                        type="regular"
                        items={dropdownItems}
                        className="dataset-export-spark-menu__dropdown"
                    />
                </div>
            ) : null}
        </div>
    );
}

/** Export control for SDK trace dataset removal modal (specific dataset name). */
export function SdkDatasetExportControl({ exportContext, isBenchmark }) {
    const {
        runExport,
        exporting,
        exportPercent,
        dropdownOpen,
        setDropdownOpen,
    } = useDatasetExport();
    const isClassification = exportContext?.usecase?.task_type === TaskType.classification;

    const handleExport = (format) => {
        const { usecase, datasetName } = exportContext || {};
        if (!usecase?._id || !datasetName) {
            sparkToast.error('Cannot download: missing task or dataset information');
            return;
        }

        runExport({
            requestData: {
                llm_usecase_id: usecase._id,
                dataset_name: datasetName,
                is_benchmark: isBenchmark,
                task_type: usecase.task_type,
                format,
            },
            filename: `${datasetName}.${format}`,
            onSuccess: () => sparkToast.success('Download started'),
            onError: (error) => {
                sparkToast.error(typeof error === 'string' ? error : 'Download failed');
            },
        });
    };

    return (
        <DatasetExportControl
            onExport={handleExport}
            isClassification={isClassification}
            exporting={exporting}
            exportPercent={exportPercent}
            dropdownOpen={dropdownOpen}
            onDropdownOpenChange={setDropdownOpen}
            buttonClassName="fs-unmask"
            menuPlacement="inline"
        />
    );
}
